<template>
  <div v-if="status === 'pending'">
    <div class="bg-white px-6 py-8 md:px-0">
      <UiSkeleton class="mb-8 h-6 w-full md:w-52" />
      <div class="flex flex-col md:flex-row">
        <div class="mb-6 md:mb-0 md:mr-32 md:w-1/2">
          <UiSkeleton class="h-72 md:h-[500px]" />
        </div>
        <div class="space-y-6 md:w-1/2">
          <UiSkeleton class="h-12 w-1/3" />
          <UiSkeleton class="h-10 w-4/5" />
          <UiSkeleton class="h-32" />
          <UiSkeleton class="h-10 w-1/5" />
          <UiSkeleton class="h-12 w-2/6" />
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <ProductContent :product="data?.product ?? null" />
  </div>
</template>

<script setup lang="ts">
import { digest } from 'ohash';

import type { ProductPageProps } from '~/components/Product/types';
import type { Locale } from '~/stores/app/types';
import type { Product } from '~/stores/product/types';

import { useAppStore } from '~/stores/app/appStore';
import { useAuthStore } from '~/stores/auth/authStore';
import { useCustomerStore } from '~/stores/customer/customerStore';
import { useProductStore } from '~/stores/product/productStore';

const runtimeConfig = useRuntimeConfig();
const { locale, t } = useI18n();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();
const localePath = useLocalePath();
const route = useRoute();

const productStore = useProductStore();
const appStore = useAppStore();
const customerStore = useCustomerStore();
const authStore = useAuthStore();

const dataKey = computed(() => `product-${props.pageResolver.data.slug}-${props.pageResolver.data.locale}`);

const props = defineProps<ProductPageProps>();

const { data, status } = await useAsyncData<{ product: Product | null }>(dataKey, async () => {
  const resolvedSlug = props.pageResolver.data.slug;
  const resolvedLocale = props.pageResolver.data.locale as Locale;

  return {
    product: await productStore.fetchProduct(resolvedSlug, resolvedLocale),
  };
});

onMounted(() => {
  dataLayer.push({
    country: appStore.currentCountryCode,
    event: 'page_view',
    hashed_email: customerStore.customer?.email ? digest(customerStore.customer?.email) : 'n/a',
    locale: locale.value,
    page_key: dataKey.value,
    user_logged: authStore.isLoggedIn,
  });
});

const title = productStore.product?.meta_title ? productStore.product.meta_title : productStore.product?.name;
const description = productStore.product?.meta_description
  ? productStore.product.meta_description
  : productStore.product?.description;

useSeoMeta({
  description,
  ogDescription: description,
  ogImage: {
    url: `${runtimeConfig.public.baseUrl}/favicon.svg`,
  },
  ogSiteName: runtimeConfig.public.baseUrl,
  ogTitle: title,
  ogType: 'website',
  title,
  twitterCard: 'summary',
  twitterDescription: description,
  twitterImage: `${runtimeConfig.public.baseUrl}/favicon.svg`,
  twitterSite: runtimeConfig.public.xTwitterAccount,
  twitterTitle: title,
});

useJsonld(() => {
  const slugs = route.params.slug ? [...route.params.slug] : ([] as string[]);
  const categorySlugs = [...slugs];
  categorySlugs.splice(-1);

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    brand: {
      '@type': 'Brand',
      name: t('general.brandName'),
    },
    category: categorySlugs.length
      ? `${runtimeConfig.public.baseUrl}${localePath(`/${categorySlugs.join('/')}`)}`
      : undefined,
    description: data.value?.product?.description ?? undefined,
    image: data.value?.product?.images?.[0],
    name: data.value?.product?.name,
    sku: data.value?.product?.sku,
    url: `${runtimeConfig.public.baseUrl}${localePath(`/${slugs.join('/')}`)}`,
  };
});
</script>
